import { DownloadIcon } from 'lucide-react'
import { useState } from 'react'

import { downloadBillingPdf } from '@/api/endpoints'
import { errorMessage } from '@/api/client'
import { Button } from '@/components/ui/button'
import { downloadBlob } from '@/lib/clipboard'

/**
 * Downloads one of our PDF routes with the session's bearer token (an authenticated fetch into a
 * blob). There is no link to hold: the browser never sees a Stripe URL.
 */
export function PdfButton({
  path,
  fallbackName,
  label = 'Download',
  fetcher = downloadBillingPdf,
  testId = 'download-pdf',
}: {
  path: string
  fallbackName: string
  label?: string
  fetcher?: (path: string) => Promise<{ blob: Blob; filename: string | undefined }>
  testId?: string
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function run() {
    setBusy(true)
    setError(null)
    try {
      const { blob, filename } = await fetcher(path)
      downloadBlob(blob, filename ?? fallbackName)
    } catch (caught) {
      setError(errorMessage(caught))
    } finally {
      setBusy(false)
    }
  }

  return (
    <span className="inline-flex flex-col items-start gap-0.5">
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={busy}
        data-testid={testId}
        onClick={() => {
          void run()
        }}
      >
        <DownloadIcon className="size-3.5" />
        {busy ? 'Preparing…' : label}
      </Button>
      {error === null ? null : (
        <span role="alert" className="text-xs text-destructive">
          {error}
        </span>
      )}
    </span>
  )
}
